import React, { useState, useEffect, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PersonIcon from "@material-ui/icons/Person";
import StarIcon from "@material-ui/icons/Star";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import LinkIcon from "@material-ui/icons/Link";
import RefreshIcon from "@material-ui/icons/Refresh";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SaveIcon from "@material-ui/icons/Save";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "600px",
      maxWidth: "95vw",
      height: "80vh",
      display: "flex",
      flexDirection: "column",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "80%",
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 44,
    "& .MuiTab-root": {
      minHeight: 44,
      fontSize: "0.8rem",
    },
  },
  tabContent: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    ...theme.scrollbarStyles,
  },
  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 700,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: 8,
    marginTop: 16,
  },
  participantItem: {
    borderRadius: 8,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  adminChip: {
    height: 20,
    fontSize: "0.65rem",
    fontWeight: 700,
    backgroundColor: "#fef3c7",
    color: "#d97706",
  },
  superAdminChip: {
    height: 20,
    fontSize: "0.65rem",
    fontWeight: 700,
    backgroundColor: "#fee2e2",
    color: "#dc2626",
  },
  inviteBox: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${theme.palette.divider}`,
    wordBreak: "break-all",
  },
  inviteLink: {
    flex: 1,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    fontFamily: "monospace",
  },
  addParticipantBox: {
    display: "flex",
    gap: 8,
    marginBottom: 12,
  },
  settingsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
}));

const TAB_INFO = 0;
const TAB_PARTICIPANTS = 1;
const TAB_INVITE = 2;

const GroupManagementModal = ({ open, onClose, group, whatsappId }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(TAB_INFO);

  // Info state
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [onlyAdminsMsg, setOnlyAdminsMsg] = useState(false);
  const [onlyAdminsInfo, setOnlyAdminsInfo] = useState(false);
  const [ephemeral, setEphemeral] = useState(0);
  const [savingInfo, setSavingInfo] = useState(false);

  // Participants state
  const [participants, setParticipants] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // jid being acted on

  // Invite state
  const [inviteLink, setInviteLink] = useState("");
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [revokingInvite, setRevokingInvite] = useState(false);

  const groupJid = group?.number ? `${group.number}@g.us` : null;

  // Load metadata when modal opens
  useEffect(() => {
    if (!open || !groupJid || !whatsappId) return;
    setTab(TAB_INFO);
    loadMetadata();
    // eslint-disable-next-line
  }, [open, groupJid]);

  const loadMetadata = async () => {
    setLoadingMeta(true);
    try {
      const { data } = await api.get(`/groups/${encodeURIComponent(groupJid)}/metadata`, {
        params: { whatsappId }
      });
      setSubject(data.subject || "");
      setDescription(data.desc || "");
      setParticipants(data.participants || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMeta(false);
    }
  };

  // ── Info Tab ─────────────────────────────────────────────────────────

  const handleSaveSubject = async () => {
    if (!subject.trim()) return;
    setSavingInfo(true);
    try {
      await api.put(`/groups/${encodeURIComponent(groupJid)}/subject`, {
        whatsappId,
        subject: subject.trim()
      });
      toast.success("Nome do grupo atualizado!");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveDescription = async () => {
    setSavingInfo(true);
    try {
      await api.put(`/groups/${encodeURIComponent(groupJid)}/description`, {
        whatsappId,
        description
      });
      toast.success("Descrição atualizada!");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSettingChange = async (setting) => {
    try {
      await api.put(`/groups/${encodeURIComponent(groupJid)}/settings`, {
        whatsappId,
        setting
      });
      if (setting === "announcement") setOnlyAdminsMsg(true);
      if (setting === "not_announcement") setOnlyAdminsMsg(false);
      if (setting === "locked") setOnlyAdminsInfo(true);
      if (setting === "unlocked") setOnlyAdminsInfo(false);
      toast.success("Configuração salva!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleEphemeralChange = async (val) => {
    setEphemeral(val);
    try {
      await api.put(`/groups/${encodeURIComponent(groupJid)}/ephemeral`, {
        whatsappId,
        expiration: val
      });
      toast.success("Mensagens temporárias configuradas!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm("Tem certeza que deseja sair do grupo?")) return;
    try {
      await api.delete(`/groups/${encodeURIComponent(groupJid)}/leave`, {
        data: { whatsappId }
      });
      toast.success("Saiu do grupo com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  // ── Participants Tab ──────────────────────────────────────────────────

  const handleAddParticipant = async () => {
    const digits = newPhone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Número inválido. Use formato com DDD: 11999887766");
      return;
    }
    setAddingParticipant(true);
    try {
      const { data } = await api.post(`/groups/${encodeURIComponent(groupJid)}/participants`, {
        whatsappId,
        participants: [digits],
        action: "add"
      });
      if (data.metadata?.participants) setParticipants(data.metadata.participants);
      setNewPhone("");
      toast.success("Participante adicionado!");
    } catch (err) {
      toastError(err);
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleParticipantAction = async (participantJid, action) => {
    setActionLoading(participantJid);
    try {
      const { data } = await api.post(`/groups/${encodeURIComponent(groupJid)}/participants`, {
        whatsappId,
        participants: [participantJid],
        action
      });
      if (data.metadata?.participants) setParticipants(data.metadata.participants);
      const labels = { remove: "Removido", promote: "Promovido a admin", demote: "Rebaixado" };
      toast.success(labels[action] || "OK");
    } catch (err) {
      toastError(err);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Invite Tab ────────────────────────────────────────────────────────

  const handleGetInvite = async () => {
    setLoadingInvite(true);
    try {
      const { data } = await api.get(`/groups/${encodeURIComponent(groupJid)}/invite`, {
        params: { whatsappId }
      });
      setInviteLink(data.link);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!window.confirm("Revogar o link atual? O link antigo deixará de funcionar.")) return;
    setRevokingInvite(true);
    try {
      const { data } = await api.delete(`/groups/${encodeURIComponent(groupJid)}/invite`, {
        data: { whatsappId }
      });
      setInviteLink(data.link);
      toast.success("Link revogado! Novo link gerado.");
    } catch (err) {
      toastError(err);
    } finally {
      setRevokingInvite(false);
    }
  };

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink)
      .then(() => toast.success("Link copiado!"))
      .catch(() => toast.error("Não foi possível copiar"));
  };

  const getParticipantNumber = (jid) => jid?.split("@")[0] || jid;

  const renderInfoTab = () => (
    <Box>
      <Typography className={classes.sectionTitle}>Nome do Grupo</Typography>
      <Box display="flex" gap={1} alignItems="flex-start" style={{ gap: 8 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={subject}
          onChange={(e) => { const v = e.target.value; setSubject(v); }}
          placeholder="Nome do grupo"
          disabled={savingInfo}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSaveSubject}
          disabled={savingInfo || !subject.trim()}
          startIcon={<SaveIcon />}
          style={{ whiteSpace: "nowrap" }}
        >
          Salvar
        </Button>
      </Box>

      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Descrição
      </Typography>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        multiline
        rows={3}
        value={description}
        onChange={(e) => { const v = e.target.value; setDescription(v); }}
        placeholder="Descrição do grupo"
        disabled={savingInfo}
      />
      <Box mt={1}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleSaveDescription}
          disabled={savingInfo}
          startIcon={<SaveIcon />}
        >
          Salvar Descrição
        </Button>
      </Box>

      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Configurações
      </Typography>
      <Box className={classes.settingsRow}>
        <Box>
          <Typography variant="body2">Somente admins enviam mensagens</Typography>
          <Typography variant="caption" color="textSecondary">Modo anúncio</Typography>
        </Box>
        <Switch
          checked={onlyAdminsMsg}
          onChange={(e) => handleSettingChange(e.target.checked ? "announcement" : "not_announcement")}
          color="primary"
          size="small"
        />
      </Box>
      <Box className={classes.settingsRow}>
        <Box>
          <Typography variant="body2">Somente admins editam informações</Typography>
          <Typography variant="caption" color="textSecondary">Bloquear edição do grupo</Typography>
        </Box>
        <Switch
          checked={onlyAdminsInfo}
          onChange={(e) => handleSettingChange(e.target.checked ? "locked" : "unlocked")}
          color="primary"
          size="small"
        />
      </Box>

      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Mensagens Temporárias
      </Typography>
      <FormControl variant="outlined" size="small" fullWidth>
        <Select
          value={ephemeral}
          onChange={(e) => handleEphemeralChange(e.target.value)}
        >
          <MenuItem value={0}>Desligado</MenuItem>
          <MenuItem value={86400}>1 dia</MenuItem>
          <MenuItem value={604800}>7 dias</MenuItem>
          <MenuItem value={7776000}>90 dias</MenuItem>
        </Select>
      </FormControl>

      <Box mt={3} pt={2} borderTop={1} borderColor="divider">
        <Button
          variant="outlined"
          style={{ color: "#dc2626", borderColor: "#dc2626" }}
          size="small"
          onClick={handleLeaveGroup}
          startIcon={<ExitToAppIcon />}
        >
          Sair do Grupo
        </Button>
      </Box>
    </Box>
  );

  const renderParticipantsTab = () => (
    <Box>
      <Box className={classes.addParticipantBox}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Número com DDD (ex: 11999887766)"
          value={newPhone}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setNewPhone(v); }}
          style={{ flex: 1 }}
          disabled={addingParticipant}
          inputProps={{ maxLength: 15 }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleAddParticipant}
          disabled={addingParticipant || newPhone.length < 10}
          startIcon={addingParticipant ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
        >
          Adicionar
        </Button>
      </Box>

      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        {participants.length} participante{participants.length !== 1 ? "s" : ""}
      </Typography>

      <List disablePadding>
        {participants.map((p) => {
          const jid = p.id || p.jid || "";
          const number = getParticipantNumber(jid);
          const isAdmin = p.admin === "admin" || p.admin === "superadmin";
          const isSuperAdmin = p.admin === "superadmin";
          const isActing = actionLoading === jid;

          return (
            <ListItem key={jid} className={classes.participantItem} dense>
              <ListItemAvatar>
                <Avatar style={{ width: 36, height: 36 }}>
                  {isSuperAdmin ? <StarIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ fontSize: "0.85rem" }}>+{number}</span>
                    {isSuperAdmin && (
                      <Chip label="Dono" size="small" className={classes.superAdminChip} />
                    )}
                    {isAdmin && !isSuperAdmin && (
                      <Chip label="Admin" size="small" className={classes.adminChip} />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                {isActing ? (
                  <CircularProgress size={18} />
                ) : (
                  <Box display="flex" style={{ gap: 4 }}>
                    {!isSuperAdmin && (
                      <>
                        {isAdmin ? (
                          <Tooltip title="Rebaixar">
                            <IconButton size="small" onClick={() => handleParticipantAction(jid, "demote")}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Promover a admin">
                            <IconButton size="small" onClick={() => handleParticipantAction(jid, "promote")}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remover do grupo">
                          <IconButton
                            size="small"
                            onClick={() => handleParticipantAction(jid, "remove")}
                            style={{ color: "#dc2626" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  const renderInviteTab = () => (
    <Box>
      <Typography className={classes.sectionTitle}>Link de Convite</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Qualquer pessoa com este link pode entrar no grupo.
      </Typography>

      {inviteLink ? (
        <Box className={classes.inviteBox}>
          <LinkIcon color="primary" />
          <Typography className={classes.inviteLink}>{inviteLink}</Typography>
          <Tooltip title="Copiar">
            <IconButton size="small" onClick={handleCopyLink}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box py={2} textAlign="center">
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Clique em "Obter Link" para gerar o link de convite.
          </Typography>
        </Box>
      )}

      <Box mt={2} display="flex" style={{ gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGetInvite}
          disabled={loadingInvite}
          startIcon={loadingInvite ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
        >
          {inviteLink ? "Atualizar Link" : "Obter Link"}
        </Button>
        {inviteLink && (
          <Button
            variant="outlined"
            style={{ color: "#dc2626", borderColor: "#dc2626" }}
            onClick={handleRevokeInvite}
            disabled={revokingInvite}
            startIcon={revokingInvite ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Revogar Link
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth={false}>
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          {group?.name || "Gerenciar Grupo"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Informações" />
        <Tab label={`Participantes${participants.length ? ` (${participants.length})` : ""}`} />
        <Tab label="Convite" />
      </Tabs>

      {loadingMeta ? (
        <Box display="flex" alignItems="center" justifyContent="center" flex={1} p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box className={classes.tabContent}>
          {tab === TAB_INFO && renderInfoTab()}
          {tab === TAB_PARTICIPANTS && renderParticipantsTab()}
          {tab === TAB_INVITE && renderInviteTab()}
        </Box>
      )}
    </Dialog>
  );
};

export default GroupManagementModal;
